import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { groupBy } from 'lodash-es'
import {
  CSSProperties,
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import {
  RequestByIdApiType,
  useAddRequestComment,
  useGetRequestCommentsList
} from '@/api/models/requests'
import { RequestStatusAlias } from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { cn } from '@/app/utils'
import { FILES_TOTAL_SIZE_EXCEEDED_MESSAGE } from '@/components/entities/requests/model/constants'
import { CompleteRequestModal } from '@/components/entities/requests/ui/RequestDetails/CompleteRequestModal/CompleteRequestModal'
import {
  FilesPreviewModal,
  UploadedFilesList,
  useUploadFiles
} from '@/components/features/upload-files'
import { useUploadProgress } from '@/components/features/upload-files/hooks/useUploadProgress'
import { AppButton } from '@/components/shared/button'
import { FileUploader } from '@/components/shared/file-uploader'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'
import { AppLoader } from '@/components/shared/loader'
import { AppTextarea } from '@/components/shared/textarea'
import { ErrorDrawerContent } from '@/components/widgets/request/ErrorDrawerContent'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import {
  MAX_COMMENT_LENGTH,
  MAX_COMMENT_LENGTH_MESSAGE_FROM
} from '@/utils/constants'
import { isFilesSizeExceeded } from '@/utils/file'
import { isForbiddenError } from '@/utils/isForbiddenError'
import { isServerValidationError } from '@/utils/parseServerErrorField'

import CommentsEmptyList from './CommentsEmptyList'
import CommentsMessagesController from './CommentsMessageController'
import CommentsSkeleton from './CommentsSkeleton'
import CommentsTitle from './CommentsTitle'

type Props = {
  request: RequestByIdApiType
  requestId: string
  refetchRequest: () => void
  className?: string
  style?: CSSProperties
}

const formatGroupDate = (date: any) => {
  return dayjs(date).calendar(null, {
    sameDay: '[Сегодня]',
    nextDay: '[Завтра], D MMMM',
    nextWeek: 'D MMMM',
    lastDay: '[Вчера], D MMMM',
    lastWeek: 'D MMMM',
    sameElse: 'D MMMM'
  })
}

const commentsMessagesWrapperId = 'commentsMessagesWrapperId'

export const Comments: FC<Props> = ({
  request,
  requestId,
  refetchRequest,
  className,
  style
}) => {
  const {
    data: messages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    isLoading
  } = useGetRequestCommentsList({ requestId })

  const { navigateToCreateRequestFromCompletedChat } = useRequestNavigation()

  const isRequestComplete = request.statusAlias === RequestStatusAlias.Complete
  const [isCompleteModalOpen, setIsCompleteModalOpen] =
    useState(isRequestComplete)

  const [requestRejectedError, setRequestRejectedError] =
    useState<Error | null>(null)
  const [validationError, setValidationError] = useState<string>()

  const { mutateAsync: addComment, isPending: isAddCommentPending } =
    useAddRequestComment(requestId)

  const [text, setText] = useState('')
  const [uploaderKey, setUploaderKey] = useState(Date.now())
  const isSubmittingRef = useRef(false)

  const { setUploadProgress, isUploading } = useUploadProgress()

  const { files, handleUpload, handleRead, handleDeleteFile, preview } =
    useUploadFiles({
      setUploadProgress,
      resetKey: uploaderKey
    })

  const messagesGroups = useMemo(() => {
    const _messages =
      messages?.toSorted(
        (a, b) =>
          new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf()
      ) || []

    return Object.entries(
      groupBy(
        _messages?.map(item => ({
          ...item,
          groupKey: dayjs(item.createdAt).format('MM.DD.YYYY')
        })),
        item => item.groupKey
      )
    )
      .map(([key, value]) => {
        return {
          title: formatGroupDate(key),
          items: value.toSorted(
            (a, b) => a.createdAt.valueOf() - b.createdAt.valueOf()
          )
        }
      })
      .reverse()
  }, [messages])

  const uploadComment = useCallback(
    async ({ text, file }: { text?: string; file?: File }) => {
      try {
        return await addComment({
          id: requestId,
          message: text || undefined,
          file: file || undefined
        })
      } catch (error) {
        const axiosError = error as AxiosError<ServerValidationError>

        if (isServerValidationError(axiosError)) {
          setValidationError(
            axiosError.response?.data.errors?.[0]?.message ||
              axiosError.response?.data.title
          )
          return
        }

        setRequestRejectedError(error as Error)
      }
    },
    [addComment, requestId, setRequestRejectedError]
  )

  const chatRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef({
    top: 0,
    prevCount: 0,
    stickToBottom: true
  })

  const handleFetchNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }

    if (chatRef.current) {
      scrollRef.current.top = chatRef.current.scrollTop
    }

    return fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // Если все сообщения помещаются на экран, вызывать запрос на получение сообщений
  useEffect(() => {
    if (
      !chatRef.current ||
      !hasNextPage ||
      isFetchingNextPage ||
      messagesGroups.length === 0
    ) {
      return
    }

    const hasOverflow =
      chatRef.current.scrollHeight > chatRef.current.clientHeight + 1

    if (!hasOverflow) {
      handleFetchNextPage()
    }
  }, [
    handleFetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    messages?.length,
    messagesGroups.length
  ])

  useEffect(() => {
    if (!chatRef.current || messagesGroups.length === 0) {
      return
    }

    const messagesCount = messages?.length ?? 0

    if (scrollRef.current.stickToBottom) {
      chatRef.current.scrollTop = 0
      scrollRef.current.stickToBottom = false
    } else if (
      messagesCount > scrollRef.current.prevCount &&
      scrollRef.current.prevCount > 0
    ) {
      chatRef.current.scrollTop = scrollRef.current.top
    }

    scrollRef.current.prevCount = messagesCount
  }, [messages?.length, messagesGroups])

  const handleAddComment = async () => {
    if (isSubmittingRef.current || isAddCommentPending || isUploading) {
      return
    }

    isSubmittingRef.current = true
    setValidationError(undefined)

    const normalizedText = text.trim()
    const readyFiles = files.filter(file => !file.loading && !file.error)

    if (!normalizedText && readyFiles.length === 0) {
      isSubmittingRef.current = false
      return
    }

    try {
      scrollRef.current.stickToBottom = true

      if (normalizedText) {
        await uploadComment({ text: normalizedText })
      }

      if (readyFiles.length > 0) {
        await Promise.all(
          readyFiles.map(file => uploadComment({ file: file.file }))
        )
        refetchRequest()
      }

      setText('')
      setUploaderKey(Date.now())
    } finally {
      isSubmittingRef.current = false
    }
  }

  const hasReadyFile = files.some(file => !file.loading && !file.error)
  const hasCommentText = text.trim().length > 0

  const filesTotalSizeExceeded = useMemo(
    () => isFilesSizeExceeded(files.map(file => file.file.size)),
    [files]
  )

  const isDownloadButtonDisabled =
    (!hasReadyFile && !hasCommentText) ||
    text.length > MAX_COMMENT_LENGTH ||
    filesTotalSizeExceeded

  const isAddCommentButtonBlocked =
    isDownloadButtonDisabled || isAddCommentPending || isUploading

  if (isLoading) {
    return <CommentsSkeleton className={className} style={style} />
  }

  if (requestRejectedError || isError) {
    return (
      <div className={cn('flex flex-1 flex-col', className)} style={style}>
        <ErrorDrawerContent
          isForbidden={isForbiddenError(requestRejectedError)}
          formError={requestRejectedError as AxiosError<ServerValidationError>}
          closeDrawer={() => setRequestRejectedError(null)}
        />
      </div>
    )
  }

  return (
    <div
      className={cn('flex min-h-0 flex-col overflow-hidden', className)}
      style={style}
    >
      <div
        ref={chatRef}
        id={commentsMessagesWrapperId}
        className="mr-[-7px] flex min-h-0 flex-grow flex-col-reverse overflow-y-auto pr-[7px]"
      >
        {messagesGroups.length > 0 ? (
          <InfiniteScroll
            inverse
            dataLength={messages?.length ?? 0}
            next={handleFetchNextPage}
            hasMore={hasNextPage}
            loader={<></>}
            scrollThreshold="400px"
            scrollableTarget={commentsMessagesWrapperId}
            className="flex flex-col-reverse"
            style={{ overflow: 'visible' }}
          >
            {messagesGroups.map(dateMessages => (
              <Fragment key={dateMessages.title}>
                <div className="flex flex-col gap-24 [&:not(:last-child)]:pb-24">
                  <CommentsTitle title={dateMessages.title} />
                  <CommentsMessagesController messages={dateMessages.items} />
                </div>
              </Fragment>
            ))}
          </InfiniteScroll>
        ) : (
          <CommentsEmptyList />
        )}
      </div>
      {!isRequestComplete && (
        <div className="flex-shrink-0 pb-24 pt-24">
          <UploadedFilesList
            files={files}
            onDelete={handleDeleteFile}
            handleFileAction={preview.handleFileAction}
            className={{
              listClassName: 'max-h-[150px] overflow-y-auto',
              rootClassName: 'bg-grey-10'
            }}
          />
          <div className="mb-24">
            <AppTextarea
              value={text}
              placeholder="Ваше сообщение"
              maxLength={MAX_COMMENT_LENGTH}
              maxLengthMessageFrom={MAX_COMMENT_LENGTH_MESSAGE_FROM}
              autoFocus
              onChange={event => setText(event.currentTarget.value)}
              borderColor="border-grey-30"
              dataTestId="chat-comment-input-id"
              counterTestId="chat-comment-counter-id"
            />
          </div>
          <div className="relative">
            <FileUploader
              onUpload={handleUpload}
              onRead={handleRead}
              buttonVariant="backless"
              buttonSize="large"
              buttonWrapperClassName="bottom-24 w-[60px]"
              isUploading={isUploading}
            />

            <div
              className={cn(
                'absolute bottom-0 right-0 w-full max-w-[calc(100%-70px)]',
                isAddCommentButtonBlocked && 'cursor-not-allowed'
              )}
            >
              <AppButton
                fluid
                variant="secondary"
                size="large"
                disabled={isDownloadButtonDisabled}
                data-testid="chat-send-button-id"
                className={
                  isAddCommentButtonBlocked ? 'pointer-events-none' : undefined
                }
                onClick={handleAddComment}
              >
                {isAddCommentPending || isUploading ? (
                  <AppLoader noText variant="primary" />
                ) : (
                  'Добавить комментарий'
                )}
              </AppButton>
            </div>
          </div>
          <FilesPreviewModal
            isModalOpen={preview.isModalOpen}
            currentFiles={preview.currentFiles}
            activeIndex={preview.activeIndex}
            onClose={preview.handleCloseModal}
          />
          <AppInputMessage
            message={
              filesTotalSizeExceeded
                ? FILES_TOTAL_SIZE_EXCEEDED_MESSAGE
                : validationError
            }
            messageClassName="text-red-70"
            iconClassName="text-red-70"
          />
        </div>
      )}

      <CompleteRequestModal
        request={request}
        requestId={requestId}
        visible={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        onCreateNewRequest={() => {
          setIsCompleteModalOpen(false)
          navigateToCreateRequestFromCompletedChat()
        }}
        refetchRequest={refetchRequest}
      />
    </div>
  )
}
